import type { Dictionary } from './types'

const nl: Dictionary = {
  common: {
    appName: 'Pivot',
    signIn: 'Inloggen met Google',
    signOut: 'Afmelden',
    loading: 'Laden…',
    save: 'Opslaan',
    cancel: 'Annuleren',
    retry: 'Opnieuw proberen',
    error: 'Er is iets misgegaan.',
    reload: 'Pagina herladen',
  },
  landing: {
    metaDescription:
      'Pivot leest elke vacature op de Belgische nicheboards, Franstalig en Nederlandstalig, scoort ze tegen je cv en toont je de handvol die past — inclusief de zijstappen die je zelf nooit zou zoeken.',
    heroTitle: 'AI nam je job af. Laat het nu je volgende job vinden.',
    subtitle:
      'Pivot leest je cv, vraagt je waar je liever zou zitten, en scoort elke vacature op de Belgische nicheboards tegen je antwoorden. Elke dag.',
    googleNote:
      'Google geeft ons je naam en e-mailadres. Het ziet je contacten, je agenda en je bestanden niet. Zo hoef je geen wachtwoord meer te verzinnen.',
    scrollCue: 'Bekijk hoe het werkt',
    shotDashboard:
      'Het dashboard van Pivot, met de gerangschikte vacatures van vandaag en hun scores',
    turnEyebrow: 'Een voorbeeld van een zijstap',
    // Zie de opmerking in en.ts. Alle drie de bestemmingen zijn verbatim uit het
    // corpus: "Fondsenwerver" en "Cultureel bemiddelaar" (11be, cultuurjobs),
    // "Sociaal-cultureel werker" (cultuurjobs).
    turnFrom1: 'Boekhouder',
    turnTo1: 'Fondsenwerver',
    turnFrom2: 'Winkelmanager',
    turnTo2: 'Cultureel bemiddelaar',
    turnFrom3: 'Leerkracht',
    turnTo3: 'Sociaal-cultureel werker',
    turnNote:
      'Dat had je nooit in een zoekbalk getypt. Elke zijstap eindigt op een echte titel van de boards die Pivot leest.',
    interviewEyebrow: 'Om te beginnen',
    interviewTitle: 'Het leest je cv en vraagt dan wat er ontbreekt',
    interviewBody:
      'Upload je cv en Pivot gaat met je in gesprek: waar je goed in bent, wat je liever niet meer doet, in welke talen je werkt, welke richting je trekt. Het kan richtingen voorstellen waar je zelf niet aan dacht. Je leest het profiel dat het schrijft na en verbetert het in gewone taal.',
    shotInterview: 'Het intakegesprek, halverwege',
    rankEyebrow: 'Elke dag',
    rankTitle: 'Elke vacature volledig gelezen, op drie assen gescoord',
    rankBody:
      'Claude opent elke vacature en leest ze van begin tot eind. Elke vacature krijgt een score voor ervaring, waarden en loon, een korte uitleg en een label: een directe stap, of een stap zijwaarts.',
    rankHonest:
      'Als er niets goed scoort, is de lijst kort of leeg. Pivot vult ze nooit op om druk te lijken.',
    shotMatch: 'Enkele vacatures, elk met haar drie scores en haar directe of zijstaplabel',
    sourcesEyebrow: 'Waar het zoekt',
    sourcesTitle: 'De boards waar je geen tijd voor hebt',
    sourcesBody:
      'De Belgische long tail: sociale sector, cultuur, verenigingen, universiteiten, de Europese wijk. Verspreid over het Frans en het Nederlands, en nooit tegen een profiel gelegd. Pivot leest ze elke ochtend allemaal.',
    sourcesFrLabel: 'Franstalig',
    sourcesNlLabel: 'Nederlandstalig',
    sourcesDeLabel: 'Duitstalig',
    sourcesEnLabel: 'Engelstalig',
    sourcesAside: 'LinkedIn en Indeed zijn geen bronnen. Daar kijk je al.',
    trustHeading: 'Niet onderhandelbaar',
    trustLangs: 'Français · Nederlands · English',
    trustConsent: 'Je cv wordt pas gelezen nadat je akkoord gaat',
    trustErasure: 'Verwijder je account en alles erin, wanneer je wil',
    trustResidency: 'Opgeslagen in de EU',
    closeTitle: 'Durf te veranderen',
    closeBody: 'Meld je aan, upload je cv, en kijk wat vandaag oplevert.',
    footerRights: 'Alle rechten voorbehouden.',
    footerPrivacy: 'Privacybeleid',
  },
  nav: {
    dashboard: 'Overzicht',
    saved: 'Bewaard',
    profile: 'Profiel',
  },
  auth: {
    emailLabel: 'E-mail',
    passwordLabel: 'Wachtwoord',
    newPasswordLabel: 'Nieuw wachtwoord',
    orDivider: 'of',
    emailSignInCta: 'Inloggen met e-mail',
    loginTitle: 'Inloggen',
    loginCta: 'Inloggen',
    loginNoAccount: 'Nog geen account?',
    loginCreateAccount: 'Account aanmaken',
    loginForgot: 'Wachtwoord vergeten?',
    signupTitle: 'Account aanmaken',
    signupCta: 'Account aanmaken',
    signupHaveAccount: 'Al een account?',
    signupSignIn: 'Inloggen',
    signupPasswordHint: 'Minstens 8 tekens.',
    forgotTitle: 'Wachtwoord vergeten',
    forgotBody:
      'Vul uw e-mailadres in: als er een account aan gekoppeld is, ontvangt u een resetlink.',
    forgotCta: 'Link versturen',
    forgotSent: 'Als dit e-mailadres bij een account hoort, is er net een resetlink verstuurd.',
    backToLogin: 'Terug naar inloggen',
    resetTitle: 'Nieuw wachtwoord',
    resetBody: 'Kies een nieuw wachtwoord voor uw account.',
    resetCta: 'Wachtwoord instellen',
    resetSuccess: 'Uw wachtwoord is bijgewerkt. U kunt nu inloggen.',
    resetInvalidToken: 'Deze link is ongeldig of verlopen. Vraag een nieuwe aan.',
    genericError: 'Er is iets misgegaan. Probeer het opnieuw.',
    sessionExpired: 'Je sessie is verlopen. Meld je opnieuw aan.',
    googleUnavailable: 'Google-aanmelding is niet geconfigureerd voor deze build. Gebruik e-mail.',
  },
  onboarding: {
    pageTitle: 'Aan de slag',
    intentTitle: 'Wat brengt je hier?',
    intentBody: 'Kies wat het best past — zo kunnen we betere vragen stellen.',
    intentPivotNewSector: 'Overstappen naar een nieuwe sector',
    intentSameSectorNewRole: 'Zelfde sector, een ander soort rol',
    intentKnowDirection: 'Ik weet waar ik naartoe wil',
    intentUnsure: 'Nog niet zeker — ik verken mijn opties',
    consentTitle: 'Voor we starten',
    consentBody:
      'Pivot kan de tekst van je cv, je antwoorden, je profiel en relevante vacatures naar Anthropic, OpenAI, Voyage AI, OpenRouter en de door OpenRouter gekozen aanbieder sturen. Lees het privacybeleid voor de volledige lijst. Pivot versleutelt zijn eigen kopie van je cv bij opslag. Pivot doet geen uitspraak over wat de genoemde aanbieders met de tekst doen. Pivot deelt je gegevens nooit met andere gebruikers.',
    consentAccept: 'Ik ga akkoord en ga verder',
    consentDecline: 'Niet nu',
    consentPolicyLink: 'Lees het volledige privacybeleid',
    declinedTitle: 'Er is niets verwerkt',
    declinedBody:
      'Er is geen cv gelezen en er zijn geen antwoorden bewaard. Er gebeurt niets tot je akkoord gaat. Kom terug wanneer je wil — of meld je af en laat het hierbij.',
    declinedBack: 'Terug en opnieuw lezen',
    stepIntent: 'Je reden',
    stepConsent: 'Toestemming',
    stepUpload: 'Je cv',
    stepInterview: 'Het gesprek',
    stepReview: 'Nakijken',
    stepsInterviewNote:
      'Dit deel is een gesprek: het duurt zo lang als nodig — meestal een handvol vragen.',
    backToProfile: 'Terug naar mijn profiel',
    uploadTitle: 'Upload je cv',
    uploadBody: 'Bij voorkeur een PDF — we halen er je achtergrond uit om het gesprek te starten.',
    uploadCta: 'Sleep hier een PDF, of kies een bestand',
    uploadFormatHint: 'PDF, tot {{limit}} MB',
    uploadInvalidType: 'Dat bestand is geen PDF. Kies een PDF.',
    uploadTooLarge:
      'Dat bestand is groter dan {{limit}} MB. Kies een kleinere PDF en probeer opnieuw.',
    uploadRejectedType:
      'De server kon dat niet als PDF lezen — een hernoemde .docx of een afbeelding geeft dit. Exporteer je cv als een echte PDF en het lukt wel.',
    uploadedAs: 'Bestand ontvangen:',
    uploadContinue: 'Verder',
    cvReading: 'We lezen je cv. Dit kan enkele minuten duren. Dat is normaal.',
    cvReadingPatience:
      'We lezen je cv nog. Dat is normaal: een model leest het echt en zoekt geen trefwoorden.',
    savedCvEyebrow: 'Al bewaard',
    savedCvBody: 'We bewaarden je cv en wat we eruit lazen. Je hoeft het niet opnieuw te sturen.',
    savedCvUploadedOn: 'Verstuurd op {{date}}',
    savedCvAnalysisLabel: 'Wat we eruit lazen',
    savedCvUse: 'Verder met dit cv',
    savedCvReplace: 'Een ander cv sturen',
    savedCvKeep: 'Het bewaarde cv houden',
    interviewTitle: 'Nog een paar vragen',
    interviewIntro: 'Laten we even praten over wat je écht zoekt.',
    interviewPlaceholder: 'Schrijf je antwoord…',
    interviewWriteOption: 'Hierover praten — schrijf je eigen antwoord',
    interviewMultiHint: 'Kies alles wat past. Hier mogen meerdere antwoorden.',
    interviewConfirm: 'Selectie versturen',
    interviewSend: 'Versturen',
    interviewSendHint: 'Enter om te versturen · Shift + Enter voor een nieuwe regel',
    interviewThinking: 'De volgende vraag wordt voorbereid',
    interviewThinkingPatience:
      'Nog bezig — elke vraag wordt gekozen op basis van wat je hebt gezegd, geen vast script.',
    interviewConsentStale:
      'Je toestemming moet vernieuwd worden voor we dit gesprek kunnen voortzetten. Controleer je profiel.',
    constraintsTitle: 'Waar kun je echt werken?',
    constraintsBody:
      'Kort en feitelijk. Dit zijn de grenzen die we niet overschrijden bij het kiezen van je matches — als een ervan je niet beperkt, zeg dat dan expliciet.',
    constraintsLocationsLabel: 'Plaatsen waar je zou werken',
    constraintsLocationsHint: 'Vink elke provincie aan waar je zou kunnen werken.',
    constraintsLocationsAnywhere: 'Overal is goed',
    provinceBrussels: 'Brussel',
    provinceAntwerp: 'Antwerpen',
    provinceLimburg: 'Limburg',
    provinceEastFlanders: 'Oost-Vlaanderen',
    provinceWestFlanders: 'West-Vlaanderen',
    provinceFlemishBrabant: 'Vlaams-Brabant',
    provinceHainaut: 'Henegouwen',
    provinceLiege: 'Luik',
    provinceNamur: 'Namen',
    provinceLuxembourg: 'Luxemburg',
    provinceWalloonBrabant: 'Waals-Brabant',
    locationChipRemove: '{{location}} verwijderen',
    constraintsAnchorLabel: 'Waar je vandaan pendelt',
    constraintsAnchorHint: 'Eén plaats. Die bepaalt hoe ver elke job echt is.',
    constraintsAnchorNone: 'Geen vaste standplaats',
    constraintsContractLabel: 'Contracten die je zou aannemen',
    constraintsContractCdi: 'Vast contract',
    constraintsContractCdd: 'Bepaalde duur',
    constraintsContractFreelance: 'Freelance',
    constraintsContractInterim: 'Interim',
    constraintsContractAny: 'Elk contract',
    constraintsSalaryLabel: 'Laagste brutomaandloon dat je zou aanvaarden',
    constraintsSalaryHint: 'Euro per maand. Niemand ziet dit, behalve de scoring.',
    constraintsSalaryNoFloor: 'Geen minimum',
    constraintsLanguagesLabel: 'Werktalen',
    constraintsLanguagesHint: 'Het niveau bepaalt of een job die de taal eist een match is.',
    constraintsLanguagesFormatHint:
      'Eén per taal, met het niveau tussen haakjes — french (native), english (fluent).',
    constraintsLanguageFrench: 'Frans',
    constraintsLanguageDutch: 'Nederlands',
    constraintsLanguageEnglish: 'Engels',
    constraintsLevelChoose: 'Kies een niveau',
    constraintsLevelNone: 'Ik spreek het niet',
    constraintsLevelBasic: 'Basis',
    constraintsLevelIntermediate: 'Matig',
    constraintsLevelFluent: 'Vloeiend',
    constraintsLevelNative: 'Moedertaal',
    constraintsLanguagesNone: 'Ik geef mijn talen liever niet op',
    constraintsSubmit: 'Bouw mijn profiel',
    constraintsIncomplete:
      'Antwoord op elke vraag, of vink het vakje aan dat zegt dat het je niet beperkt. Een leeg antwoord zou gelezen worden als "overal".',
    constraintsSaveFailed: 'We konden je antwoorden niet opslaan. Probeer opnieuw.',
    constraintsOwnEyebrow: 'Je eigen harde grenzen',
    baselineEyebrow: 'Het werk dat je echt doet',
    baselineBody:
      'Dit is het werk dat je nu doet, of het laatst deed — niet het werk dat je zoekt. We gebruiken het om een carrièrewissel van een zijstap te onderscheiden, dus het moet kloppen.',
    baselineRoleLabel: 'Je job',
    baselineDutiesLabel: 'Wat die job inhoudt',
    baselineSectorsLabel: 'Sectoren waarin je gewerkt hebt',
    baselineUnknownValue: 'Nog niet bekend',
    baselineStateUnknown:
      'We konden geen job lezen uit wat je ons gaf. Vertel ons welke, en kies waar die vandaan komt.',
    baselineStateUnconfirmed: 'Gelezen uit je cv en profiel. Bevestig het, of corrigeer het.',
    baselineStateConfirmed: 'Je hebt dit bevestigd.',
    baselineStateMissing: 'Nog geen job geregistreerd. Vertel ons welke.',
    baselineConfirm: 'Ja, dat is mijn job',
    baselineCorrect: 'Nee, corrigeer het',
    baselineKeep: 'Laat mijn job zoals die is',
    baselineRoleTitleLabel: 'Hoe heet die job',
    baselineSourceLabel: 'Waar dit vandaan komt',
    baselineSourceHint: 'Kies het item waarop deze job steunt. We bewaren alleen wat je ons gaf.',
    baselineNoSources: 'Er staat nog niets geregistreerd om een job op te baseren.',
    baselineMixedCareer: 'Mijn loopbaan is gemengd — er is geen enkele job',
    baselineListHint: 'Scheid elk item met een komma.',
    matchSetupTitle: 'Stel je zoekopdracht in',
    matchSetupBody:
      'Kort en feitelijk, zoals hierboven. Dit worden de exacte regels van je zoekopdracht — als een ervan je niet beperkt, zeg dat dan.',
    matchSetupLocationsLabel: 'Plaatsen waar je zou werken',
    matchSetupLocationsHint: 'Vink elke provincie aan waar je zou kunnen werken.',
    matchSetupLocationsNoPreference: 'Overal is goed',
    matchSetupContractLabel: 'Contracten die je zou aannemen',
    matchSetupContractNoPreference: 'Elk contract',
    matchSetupScheduleLabel: 'Werktijden',
    matchSetupScheduleHint: 'Zeg wat wel en niet werkt — beide zijn nuttig.',
    matchSetupScheduleAllowedLabel: 'Prima voor mij',
    matchSetupScheduleExcludedLabel: 'Niet voor mij',
    matchSetupScheduleDay: 'Overdag',
    matchSetupScheduleEvening: 'Avond',
    matchSetupScheduleNight: 'Nacht',
    matchSetupScheduleWeekend: 'Weekend',
    matchSetupScheduleRotatingShift: 'Wisselende shiften',
    matchSetupScheduleOnCall: 'Wachtdienst',
    matchSetupScheduleNoPreference: 'Elk rooster is goed',
    matchSetupPayLabel: 'Laagste loon dat je zou aanvaarden',
    matchSetupPayHint: 'Het bedrag, dan de munt, de periode, en of het bruto of netto is.',
    matchSetupPayAmountLabel: 'Bedrag',
    matchSetupPayCurrencyLabel: 'Munt',
    matchSetupPayPeriodLabel: 'Per',
    matchSetupPayPeriodHour: 'uur',
    matchSetupPayPeriodDay: 'dag',
    matchSetupPayPeriodMonth: 'maand',
    matchSetupPayPeriodYear: 'jaar',
    matchSetupPayPeriodUnknown: 'niet zeker',
    matchSetupPayGrossOrNetLabel: 'Bruto of netto',
    matchSetupPayGross: 'Bruto',
    matchSetupPayNet: 'Netto',
    matchSetupPayGrossOrNetUnknown: 'Niet zeker',
    matchSetupPayNoPreference: 'Geen minimum',
    matchSetupExclusionsLabel: 'Werk dat je niet zult doen',
    matchSetupExclusionsHint:
      'Eén per regel — telefonische verkoop, nachtwerk, wat voor jou geldt.',
    matchSetupEmployerLabel: 'Werkgevers waar je niet voor werkt',
    matchSetupEmployerHint:
      'Vink alleen aan wat je bedoelt. Grote groepen weigeren is niet '
      + 'hetzelfde als elk bedrijf weigeren.',
    matchSetupEmployerAllCommercial: 'Elk commercieel bedrijf',
    matchSetupEmployerLargeCorporate: 'Grote bedrijfsgroepen',
    matchSetupEmployerStrengthLabel: 'Hoe sterk is dit?',
    matchSetupEmployerStrengthHard: 'Toon me deze jobs nooit',
    matchSetupEmployerStrengthSoft: 'Toon ze lager in de lijst',
    matchSetupLanguagesLabel: 'Werktalen',
    matchSetupLanguagesHint: 'Het niveau bepaalt of een job die de taal eist een match is.',
    matchSetupSubmit: 'Zoekopdracht opslaan',
    matchSetupIncomplete:
      'Antwoord op elke vraag, of vink het vakje aan dat zegt dat het je niet beperkt. Een leeg antwoord zou gelezen worden als "overal".',
    matchSetupSaveFailed: 'We konden je antwoorden niet opslaan. Probeer opnieuw.',
    reviewTitle: 'Jouw profiel',
    reviewBody: 'Controleer en verbeter dit — het stuurt al je toekomstige matches.',
    reviewSourceEyebrow: 'Op basis van je cv en gesprek',
    reviewSummaryLabel: 'Samenvatting',
    reviewSkillsLabel: 'Vaardigheden',
    reviewWantsLabel: 'Wat je zoekt',
    reviewIntentLabel: 'Wat je hierheen bracht',
    reviewFinish: 'Bekijk mijn eerste matches',
    profileBuilding: 'We bouwen je profiel',
    profileBuildingPatience:
      'Nog bezig \u2014 dit profiel bepaalt waarop al je toekomstige matches worden beoordeeld.',
    finalizeFailed:
      'We konden je profiel nu niet opbouwen. Je antwoorden zijn bewaard — probeer het opnieuw.',
    saveFailed: 'We konden je profiel nu niet opslaan. Probeer het opnieuw.',
    entryLoadFailed: 'We konden niet bepalen waar je gebleven was. Probeer het opnieuw.',
    routeAfterInterviewFailed: 'We konden niet bepalen wat er nu volgt. Probeer het opnieuw.',
    retry: 'Opnieuw proberen',
    policyReviewTitle: 'Keur je zoekopdracht goed',
    policyReviewBody:
      'Dit is de exacte zoekopdracht die we uitvoeren. Controleer ze en keur ze goed.',
    policyReviewLocationsLabel: 'Plaatsen',
    policyReviewContractLabel: 'Contracten',
    policyReviewScheduleLabel: 'Werktijden',
    policyReviewPayLabel: 'Laagste loon',
    policyReviewExclusionsLabel: 'Uitgesloten werk',
    policyReviewDesiredWorkLabel: 'Gewenst werk',
    policyReviewUnwantedWorkLabel: 'Werk om te vermijden',
    policyReviewNoPreference: 'Geen voorkeur opgegeven',
    policyReviewBlocked: 'Antwoord nodig: {{reason}}',
    policyReviewBlockerGeneric: 'dit antwoord',
    policyReviewEmployerLabel: 'Werkgevers waar je niet voor werkt',
    policyReviewEmployerAllCommercialHard:
      'Toon me nooit een job bij een commercieel bedrijf.',
    policyReviewEmployerAllCommercialSoft:
      'Zet jobs bij commerciële bedrijven lager in mijn lijst.',
    policyReviewEmployerLargeCorporateHard:
      'Toon me nooit een job bij een grote bedrijfsgroep.',
    policyReviewEmployerLargeCorporateSoft:
      'Zet jobs bij grote bedrijfsgroepen lager in mijn lijst.',
    policyReviewEmployerTypesHard:
      'Toon me nooit een job bij deze soorten werkgever: {{types}}.',
    policyReviewEmployerTypesSoft:
      'Zet jobs bij deze soorten werkgever lager in mijn lijst: {{types}}.',
    policyReviewOrganizationPublicService: 'overheidsdienst',
    policyReviewOrganizationNonprofit: 'vzw',
    policyReviewOrganizationAcademic: 'universiteit of school',
    policyReviewOrganizationCommercial: 'commercieel bedrijf',
    policyReviewOrganizationLargeCommercial: 'grote bedrijfsgroep',
    policyReviewEmployerOtherScope: 'Werkgevers van dit type: {{scope}}.',
    policyReviewEmployerApprove: 'Deze tekst goedkeuren',
    policyReviewEmployerApproved: 'Goedgekeurd',
    policyReviewEmployerApproveFailed:
      'Deze tekst kon niet goedgekeurd worden. Herlaad de pagina en probeer opnieuw.',
    policyReviewApprove: 'Goedkeuren en verdergaan',
    policyReviewLoadFailed: 'We konden je zoekopdracht niet laden. Probeer opnieuw.',
    policyReviewChangedElsewhere:
      'Je zoekopdracht is elders gewijzigd — een ander tabblad, of sinds je deze pagina opende. Herlaad om de huidige versie te zien.',
    policyReviewApproveFailed: 'We konden je zoekopdracht niet goedkeuren. Probeer opnieuw.',
    policyReviewReload: 'Herladen',
    upgradeEvidenceTitle: 'Bekijk je ervaring',
    upgradeEvidenceBody:
      'Herdoe het interview om het bewijs voor je zoekopdracht op te bouwen. Je opgeslagen profiel blijft hetzelfde tot je klaar bent en opnieuw opslaat. De nieuwe zoekopdracht start na je goedkeuring.',
    upgradeEvidenceAction: 'Herdoe het interview',
    scoringTitle: 'Je eerste matches zoeken',
    scoringBody: 'We vergelijken actuele vacatures met je profiel. Dit duurt even.',
    scoringPatience:
      'Nog bezig \u2014 elke vacature wordt volledig gelezen, daarom duurt het even.',
    scoringStillGoing:
      'We beoordelen nog een aantal vacatures; die verschijnen zodra ze klaar zijn.',
  },
  dashboard: {
    title: 'Jouw matches van vandaag',
    matchCount: '{{count}} matches vandaag',
    directTitle: 'In lijn met je zoekopdracht',
    lateralTitle: 'Zijstappen',
    interested: 'Interesse',
    notInterested: 'Geen interesse',
    interestedMarked: 'Gemarkeerd als interesse',
    notInterestedMarked: 'Gemarkeerd als geen interesse',
    dryDayTitle: 'Niets sterk genoeg vandaag',
    dryDayBody:
      'We zeggen liever eerlijk dat er niets goeds is dan de lijst op te vullen. Kom morgen terug.',
    refineLink: 'Klopt er iets niet? Verfijn je criteria',
    preparingMatches: 'Je eerste matches worden voorbereid',
    preparingMatchesPatience:
      'Nog bezig — elke vacature wordt volledig gelezen, vandaar dat dit even duurt.',
    loadingPatience:
      'Nog bezig — het overzicht van vandaag vergelijkt de bereikte vacatures met je profiel, en dat duurt even.',
    scoreExperience: 'Ervaring',
    scoreValues: 'Waarden',
    scoreSalary: 'Loon',
    scoreNotDisclosed: 'niet vermeld',
    tagDirect: 'Direct',
    tagLateral: 'Zijstap',
    locationNotStated: 'Werkplek niet vermeld',
    viewPosting: 'Bekijk vacature',
    alsoOn: 'Ook op {{sources}}',
    showFullReasoning: 'Volledige redenering weergeven',
    analysisLanguageNote: 'Deze uitleg is in het {{language}}.',
    analysisLanguageFr: 'Frans',
    analysisLanguageNl: 'Nederlands',
    analysisLanguageEn: 'Engels',
    reportGone: 'Deze vacature bestaat niet meer',
    reportGoneSent: 'Gemeld',
  },
  matcherV2: {
    recommendedTitle: 'Bewezen matches',
    shelfTitle: 'Het bekijken waard',
    shelfBody: 'Deze vacatures hebben eerst jouw antwoord nodig voor we ze kunnen beoordelen.',
    tierStrong: 'Sterke match',
    tierPossible: 'Mogelijke match',
    tierStretch: 'Match om te overwegen',
    tierUnresolved: 'Jouw antwoord is nodig',
    concernsHeading: 'Goed om te weten',
    notesHeading: 'Niet geverifieerd',
    quotesHeading: 'Uit de vacature',
    quoteSourceNote: 'Letterlijk geciteerd, in de oorspronkelijke taal.',
    wouldApplyQuestion: 'Zou je op deze vacature solliciteren?',
    wouldApplyYes: 'Ja, ik zou solliciteren',
    wouldApplyYesMarked: 'Gemarkeerd: zou solliciteren',
    wouldApplyNo: 'Nee',
    wouldApplyNoMarked: 'Gemarkeerd: zou niet solliciteren',
    wouldApplyNoNote: 'Genoteerd. Dit verandert niets aan je andere matches.',
    checkQuestion: 'Had je dit soort werk al eerder overwogen?',
    checkYes: 'Ja',
    checkYesMarked: 'Gemarkeerd: ja, al eerder overwogen',
    checkNo: 'Nee',
    checkNoMarked: 'Gemarkeerd: nee, niet overwogen',
    checkUnsure: 'Niet zeker',
    checkUnsureMarked: 'Gemarkeerd: niet zeker',
    missingFactHeading: 'Eén ding dat we u moeten vragen',
    missingFactQuestion: 'Kunt u dit?',
    missingFactYes: 'Ja, dat kan ik',
    missingFactYesMarked: 'Gemarkeerd: u kunt dit',
    missingFactNo: 'Nee, dat kan ik niet',
    missingFactNoMarked: 'Gemarkeerd: u kunt dit niet',
    missingFactUnsure: 'Niet zeker',
    missingFactUnsureMarked: 'Gemarkeerd: niet zeker',
    missingFactUnsureNote: 'Nog open. We hebben niets over u vastgelegd.',
    missingFactReassessing: 'Bedankt. We bekijken deze vacature opnieuw met uw antwoord.',
    insufficientHeading: 'We konden deze vacature niet lezen',
    insufficientBody:
      'De tekst gaf te weinig om deze job tegen uw profiel te beoordelen. Lees ze zelf en beslis.',
    insufficientLink: 'Vacature openen',
    staleNote: 'De informatie van deze vacature is gewijzigd. Bezig met vernieuwen…',
    dryDayTitle: 'Niets bewezen vandaag',
    dryDayBody:
      'We hebben het lezen van vandaag afgerond en vonden niets de moeite waard. Kom morgen terug.',
    pendingTitle: 'Nog bezig met controleren',
    pendingBody: 'We controleren de matches van vandaag nog tegen je profiel.',
    morePending: 'Meer matches worden nog gecontroleerd.',
    reasonDemandMet: 'Je voldoet aan deze eis.',
    reasonDemandMetUnresolved:
      'Deze eis is waarschijnlijk vervuld, maar niet bevestigd door je profiel.',
    reasonDemandGap: 'Aan deze eis wordt niet voldaan.',
    reasonCoreDemandGapVerified: 'Een kerneis is bevestigd niet vervuld.',
    reasonCoreDemandGapUnresolved: 'Een kerneis is niet in beide richtingen bevestigd.',
    reasonHardFailureVerified: 'Een strikte eis is bevestigd niet vervuld.',
    reasonHardFailureUnresolved: 'Een strikte eis is niet in beide richtingen bevestigd.',
    reasonRuleConfirmed: 'Bevestigd tegenover je opgegeven voorkeuren.',
    reasonSoftRuleContradicted: 'Dit gaat in tegen een van je opgegeven voorkeuren.',
    reasonUnverifiedFact: 'De vacature vermeldt dit niet duidelijk.',
    reasonUnknownRuleFact: 'Eén voorkeur kon niet worden gecontroleerd.',
    reasonHardLocationConflict: 'De locatie botst met je eisen.',
    reasonHardContractConflict: 'Het contracttype botst met je eisen.',
    reasonHardWorkExclusionConflict: 'Dit werk staat op je uitsluitingslijst.',
    reasonRequiredLanguageKnownLow:
      'Een vereiste taal ligt onder het niveau dat je hebt opgegeven.',
    reasonUnrecognized: 'Een nieuw soort opmerking die we nog niet kunnen omschrijven.',
    applyLink: 'Open de vacature om te solliciteren',
    employerUnverified: 'De werkgever staat niet in de vacature',
    recruiterLine: 'Geplaatst door {{recruiter}}',
    contractLine: 'Contract: {{contract}}',
    careerHeading: 'Loopbaanstap',
    careerDirect: 'Zelfde werk, zelfde soort werkgever',
    careerSectorPivot: 'Zelfde werk, een ander soort werkgever',
    careerOccupationPivot: 'Ander werk, zelfde soort werkgever',
    careerOccupationAndSectorPivot: 'Ander werk, een ander soort werkgever',
    careerFamiliarOccupation: 'Werk dat je al hebt gedaan',
    careerUnresolved: 'We hebben de vergelijking met je loopbaan niet vastgesteld',
    careerSectorUnverified:
      'De sector van de werkgever staat er niet bij, dus deze vergelijking is onvolledig.',
    explanationHeading: 'Waarom deze vacature hier staat',
    transferLine: 'Je {{fact}} dekt {{demand}} die deze job vraagt.',
    blockingGap: '{{demand}} blijft onbevestigd.',
    nonblockingGap: '{{demand}} is niet aangetoond. Dat blokkeert deze aanbeveling niet.',
    budgetDelayTitle: 'De controles van vandaag staan op pauze',
    budgetDelayBody:
      'Het leesbudget van vandaag is op, dus de rest van je vacatures wacht tot morgen. Er ging niets verloren.',
    policyHoldTitle: 'Wachten op je nieuwe regel',
    policyHoldBody:
      'Je hebt een nieuwe werkgeversregel goedgekeurd. Resultaten van voor die regel worden vastgehouden tot ze eraan getoetst zijn.',
    insufficientStateTitle: 'We konden de vacatures van vandaag niet lezen',
    insufficientStateBody:
      'De vacatures die we bereikten zeiden te weinig om ze te beoordelen. Open ze zelf en beslis.',
  },
  savedSeen: {
    title: 'Bewaard',
    emptyTitle: 'Nog niets bewaard',
    emptyBody:
      'Markeer een match als interessant op je matches van vandaag en die komt hier terecht, samen met alles waarop je solliciteert.',
    sectionSaved: 'Bewaarde vacatures',
    sectionDismissed: 'Afgewezen vacatures',
    sectionNoLongerCurrent: 'Niet meer actueel',
    sectionNoLongerCurrentBody:
      'De vacature is weg, of ze voldoet niet meer aan je regels. Je notities blijven; de aanbeveling niet.',
    staleReasonDelisted: 'De vacature is offline gehaald.',
    staleReasonIneligible: 'Je regels bevelen deze vacature niet meer aan.',
    applicationStatusLabel: 'Sollicitatiestatus',
    statusNone: 'Nog niet gesolliciteerd',
    statusApplied: 'Gesolliciteerd',
    statusResponded: 'Reactie ontvangen',
    statusInterview: 'Gesprek',
    statusOffer: 'Aanbod',
    outcomePrompt: 'Al iets gehoord over deze sollicitatie?',
    outcomeYes: 'Ja',
    outcomeNotYet: 'Nog niet',
  },
  apply: {
    tailorCta: 'CV aanpassen',
    tailoring: 'Bezig met een aangepast CV opstellen…',
    tailoringPatience: 'Nog bezig — elke regel checken tegen je CV duurt even.',
    letterCta: 'Motivatiebrief schrijven',
    writingLetter: 'Bezig met een motivatiebrief opstellen…',
    writingLetterPatience: 'Nog bezig — je brief afstemmen op deze vacature duurt even.',
    errorConsent:
      'Je toestemming moet vernieuwd worden voor we je CV kunnen gebruiken. Controleer je profiel.',
    errorNoCv: 'Voeg een CV toe aan je profiel voor je er een aanpast voor een vacature.',
    errorNoCvLetter:
      'Voeg een CV toe aan je profiel voor je een motivatiebrief schrijft voor een vacature.',
    errorBudget: 'Je hebt je aanpassingen voor vandaag verbruikt. Probeer het morgen opnieuw.',
    reviewTitleCv: 'Bekijk je aangepaste CV',
    reviewTitleLetter: 'Bekijk je motivatiebrief',
    reviewHint: 'Pas aan wat je wil voor je opslaat — niets wordt bewaard tot je opslaat.',
    save: 'Opslaan',
    download: 'Downloaden als Markdown',
    backToDashboard: 'Terug naar dashboard',
    noDraft: 'Geen ontwerp om te bekijken. Begin er een vanaf een vacature op je dashboard.',
  },
  profile: {
    title: 'Mijn profiel',
    formTitle: 'Jouw gegevens',
    formBody: 'Bewerk wat Pivot over je heeft vastgelegd.',
    saveChanges: 'Wijzigingen opslaan',
    saved: 'Opgeslagen.',
    saveFailed:
      'We konden je wijzigingen nu niet opslaan. Er is niets gewijzigd — probeer opnieuw.',
    redoInterview: 'Herdoe het interview',
    reviewMatcherV2: 'Bekijk mijn zoekopdracht',
    redoConfirmBody:
      'Dit start de onboarding opnieuw vanaf het begin — je reden, toestemming, cv en het interview zelf. Je opgeslagen profiel blijft ongewijzigd tot je klaar bent en opnieuw opslaat.',
    redoConfirmGo: 'Onboarding herstarten',
    redoFailed: 'We konden de onboarding niet herstarten. Probeer opnieuw.',
    rubricTitle: 'Hoe je matches gescoord worden',
    rubricBody: 'Geschreven op basis van je cv en je interview. Pas het aan bij Verfijnen.',
    rubricVersion: 'Revisie {{version}}',
    rubricMissing: 'Rond het interview af, dan verschijnt je scoreopdracht hier.',
    rubricNotStated: 'Dit heb je ons nog niet verteld.',
    rubricSummary: 'Hoe een goede match eruitziet',
    rubricMustHaves: 'Een vacature moet hebben',
    rubricDealBreakers: 'Een vacature valt af bij',
    rubricStrengths: 'Wat jij meebrengt',
    rubricNoEvidence: 'Nog geen bewijs in het dossier',
    rubricProvenUnwanted: 'Bewezen, en niet de job die je wil',
    rubricProvenUnwantedHelp:
      'Dit telt als bewijs dat je de job aankan. Een vacature die er vooral uit bestaat, scoort laag.',
    rubricCommute: 'Waar je gaat werken',
    rubricCommuteFrom: 'Vanuit',
    rubricCommuteWithin: 'Binnen {{minutes}} min',
    rubricCommuteBy: 'Met',
    rubricCommuteRegions: "Regio's",
    rubricCommuteExamples: 'Bijvoorbeeld',
    rubricAxesHeading: 'Wat de score bepaalt',
    rubricAxisExperience: 'Score ervaring',
    rubricAxisValues: 'Score waarden',
    rubricAxisSalary: 'Score loon',
    rubricShare: '{{share}}%',
    rubricLateral: 'Zijstappen die het bekijken waard zijn',
    rubricTechnicalDepth: 'Hoe technisch je bent',
    refineTitle: 'Verfijnen',
    refineBody:
      'Vertel de adviseur wat de scoring hierboven misloopt. Hij heeft je opdracht gelezen en de vacatures die je als interessant of niet interessant markeerde.',
    refinePlaceholder: 'Bv. "hier zit nog te veel winkelvloer in"…',
    refineSend: 'Versturen',
    refining: 'Je opdracht en je matches worden gelezen',
    refiningPatience: 'Nog bezig — hij herschrijft de opdracht, hij antwoordt niet alleen maar.',
    refineYou: 'Jij',
    refineAdviser: 'Adviseur',
    refineNoChange: 'Er is nog niets veranderd — antwoord hierboven en dat gebeurt wel.',
    refineApplied: 'Toegepast vanaf je volgende matches.',
    refineUndo: 'Ongedaan maken',
    refineUndone: 'Ongedaan gemaakt',
    refineOpener:
      'Wat loopt de scoring mis? Een vacature die goed scoorde en die je nooit zou nemen, helpt me het meest.',
    exportTitle: 'Mijn gegevens downloaden',
    exportBody:
      'Alles wat Pivot over je bewaart, in één JSON-bestand: je profiel, je scores, je toestemmings- en meldingsgeschiedenis, en de cv-analyse (niet het bestand zelf). Handig voordat je je account verwijdert, of op elk moment om precies te zien wat er bewaard wordt.',
    exportButton: 'Mijn gegevens downloaden',
    exportFailed: 'We konden je export nu niet voorbereiden. Probeer het opnieuw.',
    signOutEverywhereTitle: 'Overal afmelden',
    signOutEverywhereBody:
      'Beëindigt elke actieve sessie op je account, ook deze. Iedereen die je wachtwoord kent, wordt ook afgemeld. Meld je gerust opnieuw aan — verder verandert er niets.',
    signOutEverywhereButton: 'Overal afmelden',
    signOutEverywhereFailed:
      'We konden je andere sessies niet afmelden. Je bent hier nog steeds aangemeld — probeer het opnieuw.',
    changePasswordTitle: 'Wachtwoord wijzigen',
    changePasswordBody:
      'We mailen je een herstellink in plaats van hier om een nieuw wachtwoord te vragen. De link werkt één keer, 30 minuten lang, en het nieuwe wachtwoord meldt al je andere sessies af.',
    changePasswordButton: 'Stuur me een herstellink',
    changePasswordSent: 'We hebben een herstellink naar {{email}} gestuurd. Hij werkt één keer, 30 minuten lang.',
    changePasswordFailed: 'We konden de herstellink nu niet versturen. Probeer het opnieuw.',
    deleteTitle: 'Mijn account verwijderen',
    deleteBody:
      'Dit wist je account, je profiel, je scores, je toestemming en je versleutelde cv-bestand, in één bewerking. Het gebeurt onmiddellijk en definitief — geen bedenktijd, niets is nadien nog terug te halen.',
    deleteStart: 'Mijn account verwijderen',
    deleteConfirmLabel: 'Ik begrijp dat dit definitief is en niet ongedaan gemaakt kan worden.',
    deleteConfirm: 'Alles nu wissen',
    deleteFailed:
      'We konden je account nu niet verwijderen. Er is niets gewist — probeer het opnieuw.',
    privacyLink: 'Wat we bewaren, en waarom — het privacybeleid',
    accountSectionLabel: 'Jouw account',
  },
  privacy: {
    title: 'Privacybeleid',
    version: 'Versie {{version}} · laatst bijgewerkt {{updated}}',
    intro:
      'Dit is gewone taal, geen juridisch contract. Het beschrijft wat Pivot echt met je gegevens doet.',
    whatTitle: 'Wat Pivot is',
    whatBody:
      'Een jobmatching-webapp. Je geeft je cv door en beantwoordt een kort interview; de app vergelijkt je profiel met Belgische vacatures en rangschikt ze met geconfigureerde AI-modellen.',
    collectTitle: 'Wat we verzamelen, en waarom',
    collectCv:
      'Je cv (PDF) — om je profiel op te bouwen. Versleuteld, in Google Cloud Storage; de database bewaart enkel een verwijzing, nooit het bestand.',
    collectProfile:
      'Je gestructureerd profiel (vaardigheden, ervaring, voorkeuren) — waarmee vacatures vergeleken worden.',
    collectAnswers:
      'Je antwoorden tijdens het interview — verwerkt in dat profiel. De ruwe transcriptie bestaat alleen zolang je onboarding loopt: verwijderd zodra je klaar bent, of na 30 dagen als je nooit afrondt.',
    collectAccount: 'Je e-mailadres en account-id — om je aan te melden.',
    collectInterest:
      'Welke vacatures je als interessant of niet interessant markeerde — om je latere matches bij te sturen.',
    collectConsent:
      'Het bewijs van je toestemming (beleidsversie en tijdstip) — om aan te tonen dat je akkoord ging voor we je cv aanraakten.',
    collectNever:
      'We verzamelen geen betaalgegevens, verkopen of delen je gegevens niet met adverteerders, en gebruiken ze niet om een AI-model te trainen.',
    anthropicTitle: 'AI-aanbieders',
    anthropicBody:
      'Pivot kan de tekst uit je cv, je antwoorden, je gestructureerd profiel, zoektekst die uit je profiel komt, relevante vacatureteksten, en de bijbehorende opdrachten en antwoorden naar Anthropic, OpenAI, Voyage AI of OpenRouter sturen. OpenRouter kan de aanvraag doorsturen naar een aanbieder die in dit beleid staat. Pivot heeft nog geen gegevensverwerkingsovereenkomst met deze AI-aanbieders.',
    residencyTitle: 'Waar je gegevens staan',
    residencyBody:
      'De database, de cv-opslag en de app zelf draaien allemaal in de regio europe-west1 (België) van Google Cloud.',
    storageTitle: 'Cookies en lokale opslag',
    storageBody:
      'Pivot plaatst geen eigen cookies en gebruikt geen analytics, reclame of tracking door derden. Twee zaken worden in je browser bewaard, allebei strikt noodzakelijk om de app te laten werken:',
    storageSession:
      'Je aanmeldtoken, zodat je aangemeld blijft tussen bezoeken. Verwijderd wanneer je je afmeldt.',
    storageLocale: 'De taal die je koos — Frans, Nederlands of Engels.',
    storageGoogle:
      'De knop "Inloggen met Google" voert een script uit dat door accounts.google.com wordt geserveerd. Dat script is van Google, en Google kan tijdens het uitvoeren eigen cookies op google.com lezen of plaatsen. Het laadt zodra een pagina met die knop opent, en niet pas wanneer je klikt. Aanmelden met e-mail vermijdt het volledig.',
    storageControl:
      'Je browsergegevens wissen verwijdert alles. Niets ervan profileert je, en niets ervan wordt met iemand gedeeld.',
    consentTitle: 'Toestemming',
    consentBody:
      'Voor Pivot je cv leest of persoonsgegevens naar een AI-aanbieder stuurt, wordt je uitdrukkelijke toestemming gevraagd. De huidige testaccounts en accounts van vrienden geven geen nieuwe toestemming voor deze update, en Pivot verwijdert alle ontwikkelgegevens. Een nieuw providerbeleid en uitdrukkelijke toestemming zijn vereist voor één gebruiker die geen vriend is zich inschrijft.',
    erasureTitle: 'Recht op verwijdering',
    erasureBody:
      'Je kan je account en alle bijhorende gegevens op elk moment verwijderen, met de knop "Mijn account verwijderen" op je profielpagina. Het is geen zachte verwijdering: account, profiel, scores, toestemmingen, notificatiegeschiedenis en het versleutelde cv-bestand gaan in dezelfde bewerking weg. Nadien is niets meer terug te halen — exporteer eerst wat je wil bewaren.',
    minimizationTitle: 'Gegevensminimalisatie',
    minimizationBody:
      'We bewaren enkel wat de matching nodig heeft. De ruwe interviewtranscriptie bestaat alleen zolang je onboarding loopt — verwijderd zodra je klaar bent, of na 30 dagen als je nooit afrondt — en vacatures bewaren we enkel zolang ze echt online staan.',
    contactTitle: 'Wie contacteren',
    contactBody:
      'Tot Pivot een vast privacycontact heeft, bereik je het team via het kanaal waarlangs je je aanmeldde.',
    statusTitle: 'Status van dit beleid',
    statusBody:
      'Dit is een ontwikkelbeleid voor testgebruikers en vrienden. Er is geen juridische toetsing gebeurd en er is geen gegevensverwerkingsovereenkomst met de goedgekeurde AI-aanbieders. Beide zijn vereist voor één gebruiker die geen vriend is zich inschrijft.',
    back: 'Terug',
  },
  a11y: {
    matchScores: 'Matchscores',
    scoreBand: '{{band}} op {{max}}',
    languageSwitch: 'Taal',
    logo: 'Pivot-logo',
    onboardingSteps: 'Stappen van de configuratie',
    interviewAnswer: 'Jouw antwoord',
    interviewMultiOptions: 'Antwoordopties — kies er een of meer',
    themeToggleToDark: 'Overschakelen naar donkere modus',
    themeToggleToLight: 'Overschakelen naar lichte modus',
    applyReviewTextarea: 'Inhoud van het aangepaste CV',
    skipToMain: 'Ga naar de hoofdinhoud',
    authHomeLink: 'Pivot — terug naar de homepage',
  },
}

export default nl
